import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { APP_STEPS } from "@/hooks/contest/contestSteps.js";
import { generatePseudo } from "@/utils/pseudoGenerator.js";
import { makeMockFootballLeaderboardBackend, MOCK_ODDS_LIST } from "@/mocks/mockFootballLeaderboard.js";
import { getSmartView } from "@/utils/leaderboardEngine.js";
import { mapBackendToFrontend } from "@/utils/mappers.js";
import QrCodeOverlay from "@/components/overlays/QrCodeOverlay.jsx";
import { Footer } from "@/components/Footer.jsx";
import { calculatePotentialGains } from "@/utils/logic.js";
import { BrandConfigProvider } from "@/features/brandDemo/BrandConfigContext.jsx";
import { DemoStepWelcome } from "@/components/brandDemo/DemoStepWelcome.jsx";
import { DemoGameLayout } from "@/components/brandDemo/DemoGameLayout.jsx";
import { DemoStepBetting } from "@/components/brandDemo/DemoStepBetting.jsx";
import { DemoStepForm } from "@/components/brandDemo/DemoStepForm.jsx";
import { DemoStepLeaderboard } from "@/components/brandDemo/DemoStepLeaderboard.jsx";
import { DemoPlayerModal } from "@/components/brandDemo/DemoPlayerModal.jsx";
import { DemoStepResult } from "@/components/brandDemo/DemoStepResult.jsx";
import { DemoStepSegmentation } from "@/components/brandDemo/DemoStepSegmentation.jsx";
import { generateHolyPseudo } from "@/utils/holyPseudoGenerator.js";

// Équipes génériques par langue (non spécifiques à une marque)
const DEMO_FIXTURES = {
    fr: {
        home: { shortName: "FRA", name: "France",      logoUrl: "https://flagcdn.com/w160/fr.png",     score: 1 },
        away: { shortName: "ARG", name: "Argentine",   logoUrl: "https://flagcdn.com/w160/ar.png",     score: 0 }
    },
    en: {
        home: { shortName: "ENG", name: "England",     logoUrl: "https://flagcdn.com/w160/gb-eng.png", score: 1 },
        away: { shortName: "ITA", name: "Italy",       logoUrl: "https://flagcdn.com/w160/it.png",     score: 0 }
    },
    es: {
        home: { shortName: "ESP", name: "España",      logoUrl: "https://flagcdn.com/w160/es.png",     score: 1 },
        away: { shortName: "ENG", name: "Inglaterra",  logoUrl: "https://flagcdn.com/w160/gb-eng.png", score: 0 }
    },
    it: {
        home: { shortName: "ITA", name: "Italia",      logoUrl: "https://flagcdn.com/w160/it.png",     score: 1 },
        away: { shortName: "FRA", name: "Francia",     logoUrl: "https://flagcdn.com/w160/fr.png",     score: 0 }
    },
    pt: {
        home: { shortName: "POR", name: "Portugal",    logoUrl: "https://flagcdn.com/w160/pt.png",     score: 1 },
        away: { shortName: "ESP", name: "Espanha",     logoUrl: "https://flagcdn.com/w160/es.png",     score: 0 }
    },
    de: {
        home: { shortName: "GER", name: "Deutschland", logoUrl: "https://flagcdn.com/w160/de.png",     score: 1 },
        away: { shortName: "ARG", name: "Argentinien", logoUrl: "https://flagcdn.com/w160/ar.png",     score: 0 }
    },
};

const calculatePointsForPlayer = (prediction, currentMatchScore, oddsWin, oddsExact) => {
    const STAKE = 100;
    const { home: predH, away: predA } = prediction;
    const { team1: scoreH, team2: scoreA } = currentMatchScore;

    let totalPoints = 0;
    const diffPred = predH - predA;
    const diffScore = scoreH - scoreA;
    if (Math.sign(diffPred) === Math.sign(diffScore)) totalPoints += Math.floor(STAKE * oddsWin);
    if (predH === scoreH && predA === scoreA) totalPoints += Math.floor(STAKE * oddsExact);
    return totalPoints;
};

const randomPseudo = generateHolyPseudo();

/**
 * Page de démo de prospection générique.
 * Reçoit une `config` (ex: HOLY_CONFIG) et adapte toute l'UI à la marque.
 *
 * Usage dans App.jsx :
 *   <Route path="/holy"     element={<BrandDemoPage config={HOLY_CONFIG} />} />
 *   <Route path="/newbrand" element={<BrandDemoPage config={NEW_BRAND_CONFIG} />} />
 */
export default function BrandDemoPage({ config }) {
    const { i18n } = useTranslation();
    const { demoContest } = config;

    const [initialConfig] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const targetLang = (params.get("lang") && DEMO_FIXTURES[params.get("lang")])
            ? params.get("lang")
            : (DEMO_FIXTURES[i18n.language] ? i18n.language : "fr");

        const teams = DEMO_FIXTURES[targetLang];
        const langRewards = demoContest.rewardsByLang?.[targetLang] ?? {};
        const finalBarName = params.get("bar") || demoContest.place.name;

        return {
            lang: targetLang,
            data: {
                fixture: {
                    ...demoContest.fixture,
                    homeTeam: teams.home,
                    awayTeam: teams.away,
                },
                participantUi: {
                    rewardLabel: langRewards.rewardLabel ?? demoContest.defaultRewardLabel,
                },
                winnerReward: langRewards.winnerReward ?? demoContest.defaultWinnerReward,
                loserReward:  langRewards.loserReward  ?? demoContest.defaultLoserReward,
                coverUrl: demoContest.coverUrl,
                place: { ...demoContest.place, name: finalBarName },
            },
        };
    });

    useEffect(() => {
        if (i18n.language !== initialConfig.lang) {
            i18n.changeLanguage(initialConfig.lang);
        }
    }, [initialConfig.lang, i18n]);

    const [contestData, setContestData] = useState(initialConfig.data);
    const [step, setStep] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const forcedStep = params.get("step")?.toUpperCase();
        return (forcedStep && APP_STEPS[forcedStep]) ? APP_STEPS[forcedStep] : APP_STEPS.WELCOME;
    });

    const [userScore, setUserScore] = useState({ team1: 1, team2: 2 });
    const [userInfo, setUserInfo]   = useState({ pseudo: randomPseudo, email: "test@gojokodo.fr" });
    const [isLoading, setIsLoading] = useState(false);

    const [ghostCursor, setGhostCursor] = useState({
        active: false,
        x: window.innerWidth / 2,
        y: window.innerHeight + 100,
        clicking: false,
    });

    const calculations = useMemo(() => calculatePotentialGains(userScore, MOCK_ODDS_LIST), [userScore]);

    const [matchScore, setMatchScore] = useState({ team1: 1, team2: 2 });

    const [leaderboardBackend, setLeaderboardBackend] = useState(() =>
        makeMockFootballLeaderboardBackend(5, { meIndex: 4, startId: 30, targetScore: { home: 1, away: 2 } })
    );

    const leaderboard = useMemo(
        () => getSmartView(mapBackendToFrontend(leaderboardBackend)),
        [leaderboardBackend]
    );

    const [overlays, setOverlays] = useState({
        goal: false,
        qr: { isOpen: false, title: "", url: "" },
        selectedPlayer: null,
    });

    const handleScoreChange = useCallback((team, op) => {
        setUserScore((prev) => {
            const current = prev[team];
            let next = op === "plus" ? current + 1 : current - 1;
            if (next < 0) next = 0;
            return { ...prev, [team]: next };
        });
    }, []);

    const processSubmission = useCallback((overrideScore = null) => {
        setIsLoading(true);
        const finalScore = overrideScore || userScore;

        setTimeout(() => {
            setLeaderboardBackend((prevList) => {
                const newList = prevList.map((p) => ({ ...p }));
                const me = newList.find((p) => p.isMe);

                if (me) {
                    me.pseudo             = userInfo.pseudo;
                    me.predictionHome     = finalScore.team1;
                    me.predictionAway     = finalScore.team2;
                    me.oddsExactScore     = parseFloat(calculations.oddsScore);
                    me.oddsWinner         = parseFloat(calculations.oddsWin);
                    me.totalPoints = calculatePointsForPlayer(
                        { home: finalScore.team1, away: finalScore.team2 },
                        matchScore,
                        me.oddsWinner,
                        me.oddsExactScore
                    );
                }

                newList.sort((a, b) => b.totalPoints - a.totalPoints);
                newList.forEach((p, idx) => (p.rank = idx + 1));
                return newList;
            });

            setIsLoading(false);
            setStep(APP_STEPS.SEGMENTATION);
        }, 600);
    }, [userInfo, userScore, calculations, matchScore]);

    const handleStealthAction = useCallback(() => {
        if (matchScore.team1 === 1) {
            setOverlays((prev) => ({ ...prev, goal: true }));
            if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 500]);

            setTimeout(() => {
                const newScore = { team1: 2, team2: 2 };
                setMatchScore(newScore);
                setContestData((prev) => ({
                    ...prev,
                    fixture: {
                        ...prev.fixture,
                        homeTeam: { ...prev.fixture.homeTeam, score: 2 },
                        awayTeam: { ...prev.fixture.awayTeam, score: 2 },
                    },
                }));
                setLeaderboardBackend((prevList) => {
                    const newList = prevList.map((p) => ({
                        ...p,
                        totalPoints: calculatePointsForPlayer(
                            { home: p.predictionHome, away: p.predictionAway },
                            newScore,
                            p.oddsWinner,
                            p.oddsExactScore
                        ),
                    }));
                    newList.sort((a, b) => b.totalPoints - a.totalPoints);
                    newList.forEach((p, idx) => (p.rank = idx + 1));
                    return newList;
                });
                setTimeout(() => setOverlays((prev) => ({ ...prev, goal: false })), 4000);
            }, 500);

        } else if (matchScore.team1 === 2) {
            if (navigator.vibrate) navigator.vibrate([50, 50]);
            const me = leaderboardBackend.find((p) => p.isMe);
            const isWinner = me && me.rank === 1;
            setStep(isWinner ? APP_STEPS.RESULT_WIN : APP_STEPS.RESULT_LOSE);
        }
    }, [matchScore, leaderboardBackend]);

    const resetDemo = () => {
        setStep(APP_STEPS.WELCOME);
        setContestData(initialConfig.data);
        setUserScore({ team1: 1, team2: 2 });
        setMatchScore({ team1: 1, team2: 2 });
        setUserInfo({ pseudo: generatePseudo(), email: "test@gojokodo.fr" });
        setLeaderboardBackend(makeMockFootballLeaderboardBackend(5, {
            meIndex: 4, startId: 30, targetScore: { home: 1, away: 2 },
        }));
    };

    // Séquence de clics automatisée (pilotée depuis un iframe parent pour les démos)
    useEffect(() => {
        const simulateClick = (elementId, actionCallback, delayBefore, fallbackX, fallbackY) => {
            return new Promise((resolve) => {
                setTimeout(() => {
                    const el = document.getElementById(elementId);
                    let targetX = fallbackX;
                    let targetY = fallbackY;
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        targetX = rect.left + rect.width / 2;
                        targetY = rect.top + rect.height / 2;
                    }
                    setGhostCursor((prev) => ({ ...prev, x: targetX, y: targetY }));
                    setTimeout(() => {
                        setGhostCursor((prev) => ({ ...prev, clicking: true }));
                        setTimeout(() => {
                            setGhostCursor((prev) => ({ ...prev, clicking: false }));
                            actionCallback();
                            resolve();
                        }, 200);
                    }, 600);
                }, delayBefore);
            });
        };

        const handleMessage = async (event) => {
            if (event.data.type === "SIMULATE_START") {
                setGhostCursor({ active: true, x: window.innerWidth / 2, y: window.innerHeight + 100, clicking: false });
                await simulateClick("demo-btn-start", () => setStep(APP_STEPS.GAME), 500, window.innerWidth / 2, window.innerHeight - 80);
                setTimeout(() => setGhostCursor((prev) => ({ ...prev, active: false })), 500);
            }

            if (event.data.type === "SIMULATE_BET") {
                setGhostCursor({ active: true, x: window.innerWidth / 2, y: window.innerHeight + 100, clicking: false });
                await simulateClick("demo-btn-plus", () => {
                    const btn = document.getElementById("demo-btn-plus");
                    if (btn) btn.click();
                    else handleScoreChange("team1", "plus");
                }, 300, window.innerWidth / 2 - 50, 300);
                await new Promise((r) => setTimeout(r, 800));
                await simulateClick("demo-btn-validate", () => {
                    const btn = document.getElementById("demo-btn-validate");
                    if (btn) btn.click();
                    else setStep(APP_STEPS.FORM);
                }, 100, window.innerWidth / 2, window.innerHeight - 100);
                setTimeout(() => setGhostCursor((prev) => ({ ...prev, active: false })), 500);
            }

            if (event.data.type === "SIMULATE_FORM") {
                setGhostCursor({ active: true, x: window.innerWidth / 2, y: window.innerHeight + 100, clicking: false });
                await simulateClick("demo-btn-submit", () => processSubmission({ team1: 2, team2: 2 }), 400, window.innerWidth / 2, window.innerHeight - 100);
                setTimeout(() => setGhostCursor((prev) => ({ ...prev, active: false })), 500);
            }

            if (event.data.type === "SIMULATE_SEGMENTATION") {
                setGhostCursor({ active: true, x: window.innerWidth / 2, y: window.innerHeight + 100, clicking: false });
                await simulateClick("demo-btn-product", () => {
                    const btn = document.getElementById("demo-btn-product");
                    if (btn) btn.click();
                }, 400, window.innerWidth / 2 - 60, window.innerHeight / 2 - 20);
                await simulateClick("demo-btn-segment", () => setStep(APP_STEPS.LEADERBOARD), 1000, window.innerWidth / 2, window.innerHeight - 80);
                setTimeout(() => setGhostCursor((prev) => ({ ...prev, active: false })), 500);
            }

            if (event.data.type === "SIMULATE_GOAL") handleStealthAction();
            if (event.data.type === "SIMULATE_WIN")  setStep(APP_STEPS.RESULT_WIN);
            if (event.data.type === "SIMULATE_LOSS") setStep(APP_STEPS.RESULT_LOSE);
            if (event.data.type === "RESET_DEMO") {
                setGhostCursor((prev) => ({ ...prev, active: false }));
                resetDemo();
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [handleScoreChange, processSubmission, handleStealthAction]);

    const handleFormSubmit = (e) => {
        e.preventDefault();
        processSubmission();
    };

    const updateOverlay = (key, value) => setOverlays((prev) => ({ ...prev, [key]: value }));

    const handleShowQr = (reward) => {
        updateOverlay("qr", { isOpen: true, title: reward.title, url: reward.code || "DEMO-CODE" });
    };

    const StepWelcome = config.components?.StepWelcome ?? DemoStepWelcome;
    const StepResult  = config.components?.StepResult  ?? DemoStepResult;

    const renderContent = () => {
        if (step === APP_STEPS.WELCOME) {
            return <StepWelcome onStart={() => setStep(APP_STEPS.GAME)} contestData={contestData} />;
        }

        if (step === APP_STEPS.GAME) {
            return (
                <DemoStepBetting
                    userScore={userScore}
                    matchScore={matchScore}
                    handleScoreChange={handleScoreChange}
                    calculations={calculations}
                    onValidate={() => setStep(APP_STEPS.FORM)}
                />
            );
        }

        if (step === APP_STEPS.FORM) {
            return (
                <DemoStepForm
                    userInfo={userInfo}
                    setUserInfo={setUserInfo}
                    handleFormSubmit={handleFormSubmit}
                    userScore={userScore}
                    isLoading={isLoading}
                    apiError={null}
                    onBack={() => setStep(APP_STEPS.GAME)}
                    isDemo={true}
                />
            );
        }

        if (step === APP_STEPS.SEGMENTATION) {
            return <DemoStepSegmentation onNext={() => setStep(APP_STEPS.LEADERBOARD)} />;
        }

        if (step === APP_STEPS.LEADERBOARD) {
            return (
                <div className="relative">
                    <div
                        className="absolute -top-32 left-0 w-full h-24 z-50 cursor-pointer"
                        onClick={handleStealthAction}
                    />
                    <DemoStepLeaderboard
                        leaderboard={leaderboard}
                        isGoalFlashing={overlays.goal}
                        setSelectedPlayer={(player) => updateOverlay("selectedPlayer", player)}
                    />
                </div>
            );
        }

        if (step === APP_STEPS.RESULT_WIN || step === APP_STEPS.RESULT_LOSE) {
            const r = step === APP_STEPS.RESULT_WIN ? contestData.winnerReward : contestData.loserReward;
            return (
                <div className="min-h-full">
                    <StepResult
                        contestId={null}
                        place={contestData.place}
                        status={step}
                        reward={r}
                        pseudo={userInfo.pseudo}
                        triggerAnimation={step === APP_STEPS.RESULT_WIN}
                        onShowQr={handleShowQr}
                        isPreview={true}
                    />
                </div>
            );
        }

        return <div className="p-4 pt-10 text-center">Fin de la démo.</div>;
    };

    const stepsWithLayout = [APP_STEPS.GAME, APP_STEPS.FORM, APP_STEPS.LEADERBOARD, APP_STEPS.MISSED];

    return (
        <BrandConfigProvider config={config}>
            <div className="h-screen overflow-y-auto font-sans bg-[#EBE1CB] text-gray-800 relative overflow-x-hidden">
                <style>{`
                    .scroll-smooth { scroll-behavior: smooth; }
                    @keyframes popInFlex { 0% { opacity: 0; transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
                    .animate-pop-in-flex { animation: popInFlex 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                `}</style>

                <div className="max-w-110 mx-auto min-h-full relative shadow-2xl bg-white/50">
                    {stepsWithLayout.includes(step) ? (
                        <DemoGameLayout
                            contestData={contestData}
                            matchScore={matchScore}
                            isGoalFlashing={overlays.goal}
                        >
                            {renderContent()}
                        </DemoGameLayout>
                    ) : (
                        renderContent()
                    )}

                    <Footer />
                    <QrCodeOverlay
                        isOpen={overlays.qr.isOpen}
                        title={overlays.qr.title}
                        url={overlays.qr.url}
                        onClose={() => updateOverlay("qr", { ...overlays.qr, isOpen: false })}
                    />
                    {overlays.selectedPlayer && (
                        <DemoPlayerModal
                            player={overlays.selectedPlayer}
                            fixtureStartingAt={contestData.fixture.startingAt}
                            onClose={() => updateOverlay("selectedPlayer", null)}
                        />
                    )}
                </div>

                {/* CURSEUR FANTÔME ANIMÉ */}
                <AnimatePresence>
                    {ghostCursor.active && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{
                                opacity: 1,
                                x: ghostCursor.x - 24,
                                y: ghostCursor.y - 24,
                                scale: ghostCursor.clicking ? 0.8 : 1,
                            }}
                            exit={{ opacity: 0 }}
                            transition={{
                                x: { type: "spring", stiffness: 60, damping: 15 },
                                y: { type: "spring", stiffness: 60, damping: 15 },
                                scale: { duration: 0.15 },
                            }}
                            className="fixed top-0 left-0 z-9999 pointer-events-none w-12 h-12 bg-white/50 border-2 border-white rounded-full shadow-[0_0_20px_rgba(0,0,0,0.3)] backdrop-blur-sm flex items-center justify-center"
                        >
                            <div className={`w-4 h-4 rounded-full bg-action-green transition-opacity duration-200 ${ghostCursor.clicking ? "opacity-100 shadow-[0_0_10px_#10B981]" : "opacity-0"}`} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </BrandConfigProvider>
    );
}
